import React, { useEffect, useState, type HTMLAttributes } from "react";
import { clsx } from "clsx";
import { parseResources, toResourceCards, type Data, GetList, types } from "@module/resource";
import { useStore } from "@nanostores/react";
import { useTranslatedRelative, translateGlossaries as ts } from "@i18next/client/utils";
// widget
import Pagination from "@react/widget/Pagination";
import ResourcesCard from "@react/widget/ResourcesCard";
import Img from "@react/widget/Img";
import { Chip } from "@nextui-org/chip";
import { H2 } from "@react/widget/Headline";
import Button from "@react/widget/Button";
import { excludes, sort, topic, type } from "@module/resource/store";
import moment, { parseISO8601 } from '@module/timezone';

interface Props extends HTMLAttributes<HTMLDivElement> {
    currentSite: Page.Site;
    init?: Data;
    page?: number;
    label?: Record<string, any>;
    glossaries?: Record<string, string>;
}

var first = true;
const Cards: React.FC<Props> = ({ currentSite, className, init = { entryCount: 0, entries: [] }, page:_page = 1,label = { prev: "Prev", next: "Next" }, glossaries, children, ...props }) => {
    const tr = useTranslatedRelative(currentSite.locale);
    const $excludes = useStore(excludes);
    const $type = useStore(type);
    const $topic = useStore(topic);
    const $sort = useStore(sort);
    const [loading, setLoading] = useState(false);
    const limit = 18;
    const [page, setPage] = useState(_page);
    const [total, setTotal] = useState(Math.ceil(init.entryCount / limit));
    const [entries, setEntries] = useState(parseResources(init.entries));
    const render = () => {
        setLoading(true);
        GetList({
            locale: currentSite.locale,
            type: !!$type ? types[$type]?.section : undefined,
            excludes: $excludes,
            topic: $topic,
            sort: $sort,
            page: page,
            limit: limit,
        }, (res) => {
            setTotal(Math.ceil(res.entryCount / limit));
            setEntries(parseResources(res.entries));
            setLoading(false);
        }, () => {
            setLoading(false);
        });
    }
    useEffect(() => {
        if (!first) {
            if (page === 1) render();
            else setPage(_page);
        }
    }, [$excludes, $topic, $sort]);
    useEffect(() => {
        if (!first) {
            render();
        }
    }, [page]);
    useEffect(() => { first = false; }, []);
    const now = moment();
    if (!entries || !entries.length) {
        return <h4 className="text-center w-full">{ts(glossaries, "No resources were found")}.</h4>;
    } else {
        return <>
            <div className={clsx("grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-x-4 gap-y-20", className)} {...props}>
                {entries?.map((entry, i) => {
                    const { eventInformation: [eventInformation] = [] } = entry;
                    const startTime = parseISO8601(eventInformation?.startTime, currentSite.locale);
                    if (entry.sectionHandle === "workshops" && (!!startTime && startTime < now)) {
                        return null;
                    }
                    return (
                        <ResourcesCard
                            isLoaded={!loading}
                            key={`resource-${i}`}
                            ebookCategory={entry.ebookCategory}
                            type={entry.type}
                            gs={glossaries}
                            eventThumbnail={(
                                <a href={entry.url} className={clsx("block absolute inset-0 rounded-t-lg overflow-hidden", entry.type === "eBook" && "bg-gray-50 border border-b-0")}>
                                    {entry?.hero?.image && (
                                        <Img className={clsx(entry.type === "eBook"
                                            ? "h-auto w-auto mx-auto max-h-[85%] max-w-[60%] top-auto"
                                            : "w-full h-full object-cover",
                                            "transition duration-500 absolute inset-0"
                                        )}
                                            src={entry.hero.image}
                                            width={(entry.type === "eBook") ? 234 : 389}
                                            height={(entry.type === "eBook") ? 186 : 218}
                                            fit={(entry.type === "eBook") ? "inside" : "cover"}
                                        />
                                    )}
                                </a>
                            )}
                            topics={entry.topics?.map(t => (
                                <a key={t.slug} href={tr(`/resources/topic/${t.slug}`)}>
                                    <Chip
                                        size="sm"
                                        variant="flat"
                                        classNames={{
                                            base: `rounded-md border-1 transition hover:grayscale`,
                                        }}
                                        style={{
                                            borderColor: t.color,
                                            color: t.color,
                                            backgroundColor: `${t.color}0f`,
                                        }}
                                    >
                                        {t.title}
                                    </Chip>
                                </a>
                            ))}
                            summary={(
                                <a href={entry.url}>
                                    <H2
                                        size="lg"
                                        className="hover:text-primary transition duration-500 line-clamp-2"
                                        title={entry.hero?.title}
                                    >{entry.hero?.title}
                                    </H2>
                                </a>
                            )}
                            tags={entry.tags?.map(t => (
                                <Chip
                                    key={t.slug}
                                    size="sm"
                                    variant="faded"
                                    classNames={{
                                        base: `rounded-md bg-transparent`,
                                    }}
                                >
                                    {t.title}
                                </Chip>
                            ))}
                            date={
                                entry.sectionHandle === "webinars" ?
                                    (!!startTime && startTime > now) && <div className="space-y-2 mt-4">{!!eventInformation?.customTime ? <div dangerouslySetInnerHTML={{ __html: eventInformation?.customTime }} /> : startTime?.format('dddd, MMMM Do hh:mm A z')}</div>
                                    :
                                    entry.sectionHandle === "workshops" && (!!startTime && startTime > now) && <div className="space-y-2 mt-4">{startTime?.format('dddd, MMMM Do hh:mm A z')}</div>
                            } cta={(
                                <Button
                                    className="!px-0 !justify-start !pb-0 mt-4"
                                    size="lg"
                                    href={entry.url}
                                    icon="arrow-right-long"
                                >{
                                    entry.type == "eBook" ?
                                        ts(glossaries, `Get the ${entry.ebookCategory || "eBook"}`)
                                        :
                                        ((entry.sectionHandle === "webinars") ?
                                            ts(glossaries, (!!startTime && startTime > now) ? "Register Now" : "Watch On-Demand")
                                            :
                                            ts(glossaries, (entry.sectionHandle === "workshops") ? "Register Now" : "View Event Details")
                                        )
                                }</Button>
                            )}
                            className="max-w-sm md:max-w-full mx-auto w-full [&:hover_img]:scale-110 rounded-lg overflow-hidden"
                        />
                    );
                }).filter(e => !!e)}
            </div>
            <Pagination label={label} page={page} total={total} onChange={p => { setPage(p); toResourceCards() }} />
        </>;
    }
}

export default Cards;